import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { UIMessagingService } from '../../services/uimessaging.service';
import { UploadLinkRow, UploadLinksService } from '../../services/upload-links.service';

export interface HospitalUploadLinkDialogData {
  /** Key of the matter (the `patients` document). The link cannot exist without it. */
  patientCaseName: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Staff dialog opened from the matter screen: creates a one-time upload link for a hospital and lists the links of the matter.
 * The link is shown once; the server keeps only its hash.
 */
@Component({
  selector: 'app-hospital-upload-link-dialog',
  templateUrl: './hospital-upload-link-dialog.component.html',
  styleUrls: ['./hospital-upload-link-dialog.component.scss'],
})
export class HospitalUploadLinkDialogComponent implements OnInit {
  readonly ttlOptions = [3, 7, 14];

  hospitalName = '';
  recipientEmail = '';
  ttlDays = 7;

  creating = false;
  loading = false;
  newLinkUrl: string | null = null;
  newLinkRecipient = '';
  links: UploadLinkRow[] = [];

  constructor(
    private dialogRef: MatDialogRef<HospitalUploadLinkDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: HospitalUploadLinkDialogData,
    private uploadLinks_$: UploadLinksService,
    private ui_$: UIMessagingService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadLinks();
  }

  get canCreate(): boolean {
    return !this.creating && this.hospitalName.trim().length > 0 && EMAIL_PATTERN.test(this.recipientEmail.trim());
  }

  async loadLinks(): Promise<void> {
    this.loading = true;
    try {
      const { links } = await this.uploadLinks_$.list(this.data.patientCaseName);
      this.links = links.sort((a, b) => b.createdAtMs - a.createdAtMs);
    } catch (err) {
      console.error('Could not load the upload links', err);
      this.ui_$.toastMessage('Could not load the upload links.', 'OK');
    } finally {
      this.loading = false;
    }
  }

  async create(): Promise<void> {
    if (!this.canCreate) return;
    this.creating = true;
    try {
      const { token } = await this.uploadLinks_$.create({
        patientCaseName: this.data.patientCaseName,
        hospitalName: this.hospitalName.trim(),
        recipientEmail: this.recipientEmail.trim(),
        ttlDays: this.ttlDays,
      });
      // Shown once: the server stores only the hash of the token and cannot show it again.
      this.newLinkUrl = this.uploadLinks_$.linkUrl(token);
      this.newLinkRecipient = this.recipientEmail.trim();
      this.hospitalName = '';
      this.recipientEmail = '';
      await this.loadLinks();
    } catch (err) {
      console.error('Could not create the upload link', err);
      const code = String((err as any)?.code || '');
      this.ui_$.toastMessage(
        code.includes('permission-denied')
          ? 'You cannot create upload links for this matter.'
          : code.includes('invalid-argument')
            ? 'Check the hospital name and the email address.'
            : 'Could not create the upload link.',
        'OK',
      );
    } finally {
      this.creating = false;
    }
  }

  async copyLink(): Promise<void> {
    if (!this.newLinkUrl) return;
    try {
      await navigator.clipboard.writeText(this.newLinkUrl);
      this.ui_$.toastMessage('Link copied.', 'OK');
    } catch (err) {
      this.ui_$.toastMessage('Could not copy. Select the link and copy it by hand.', 'OK');
    }
  }

  selectLinkText(event: FocusEvent): void {
    (event.target as HTMLInputElement).select();
  }

  async revoke(row: UploadLinkRow): Promise<void> {
    try {
      await this.uploadLinks_$.revoke(row.linkId);
      await this.loadLinks();
    } catch (err) {
      console.error('Could not revoke the upload link', err);
      this.ui_$.toastMessage('Could not revoke the link.', 'OK');
    }
  }

  openCase(row: UploadLinkRow): void {
    if (!row.imagingCaseId) return;
    this.dialogRef.close();
    this.router.navigate(['imaging_cases', row.imagingCaseId]);
  }

  close(): void {
    this.dialogRef.close();
  }
}
